"use client";

import { useTrainerColors } from "./trainer-colors-context";
import { useInstitutionButtonColors } from "./institution-button-colors-context";
import { useFacultyColors } from "./faculty-colors-context";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface VendorButtonColorsContextType {
  addButtonColor: string;
  cancelButtonColor: string;
  setAddButtonColor: (color: string) => void;
  setCancelButtonColor: (color: string) => void;
  resetColors: () => void;
}

export const VendorButtonColorsContext = createContext<
  VendorButtonColorsContextType | undefined
>(undefined);

const DEFAULT_ADD_COLOR = "#0070f3"; // blue-600 (updated from green)
const DEFAULT_CANCEL_COLOR = "#6b7280"; // gray-500

interface VendorButtonColorsProviderProps {
  children: ReactNode;
}

export function VendorButtonColorsProvider({
  children,
}: VendorButtonColorsProviderProps) {
  const [addButtonColor, setAddButtonColorState] =
    useState<string>(DEFAULT_ADD_COLOR);
  const [cancelButtonColor, setCancelButtonColorState] =
    useState<string>(DEFAULT_CANCEL_COLOR);

  // Load colors from backend and localStorage on mount
  useEffect(() => {
    const fetchColors = async () => {
      let userRole: string | null = null;
      // Only fetch colors if user is authenticated
      if (typeof window !== "undefined") {
        const isAuthenticated = localStorage.getItem("isAuthenticated");
        userRole = localStorage.getItem("userRole");
        
        // Don't fetch if not authenticated or not a vendor/vendorteam
        if (isAuthenticated !== "true" || (userRole !== "vendor" && userRole !== "vendorteam")) {
          console.log("🎨 Skipping color fetch - user not authenticated as vendor or vendorteam");
          return;
        }
      }

      try {
        // Import vendor service dynamically to avoid circular dependencies
        const { vendorService } = await import("@/services/vendor");
        
        console.log("🎨 Fetching vendor colors from backend...");
        
        // Try to fetch from backend first
        const response = userRole === "vendorteam"
          ? await vendorService.getTeamColors()
          : await vendorService.getColors();
        
        console.log("🎨 Vendor colors response:", response);
        
        if (response.success === 1 && response.data) {
          const { primary_colour, secondary_colour } = response.data;
          
          console.log("🎨 Fetched colors:", { primary_colour, secondary_colour });
          
          // Use fetched colors if they exist, otherwise use defaults
          if (primary_colour) {
            console.log("🎨 Setting primary color:", primary_colour);
            setAddButtonColorState(primary_colour);
            // Sync to localStorage
            if (typeof window !== "undefined") {
              localStorage.setItem("vendor-add-button-color", primary_colour);
            }
          } else {
            console.log("🎨 Primary color is null, using default");
          }
          if (secondary_colour) {
            console.log("🎨 Setting secondary color:", secondary_colour);
            setCancelButtonColorState(secondary_colour);
            // Sync to localStorage
            if (typeof window !== "undefined") {
              localStorage.setItem("vendor-cancel-button-color", secondary_colour);
            }
          } else {
            console.log("🎨 Secondary color is null, using default");
          }
          return; // Backend is source of truth, exit
        } else {
          console.log("🎨 Backend response unsuccessful or no data:", response);
        }
      } catch (error) {
        console.error("❌ Failed to fetch vendor colors from backend:", error);
      }

      // Fallback to localStorage if backend fetch failed or returned no colors
      console.log("🎨 Falling back to localStorage");
      if (typeof window !== "undefined") {
        const savedAddColor = localStorage.getItem("vendor-add-button-color");
        const savedCancelColor = localStorage.getItem(
          "vendor-cancel-button-color",
        );

        console.log("🎨 localStorage colors:", { savedAddColor, savedCancelColor });

        if (savedAddColor) {
          setAddButtonColorState(savedAddColor);
        }
        if (savedCancelColor) {
          setCancelButtonColorState(savedCancelColor);
        }
      }
    };

    fetchColors();
  }, []);

  const setAddButtonColor = (color: string) => {
    setAddButtonColorState(color);
    if (typeof window !== "undefined") {
      localStorage.setItem("vendor-add-button-color", color);
    }
  };

  const setCancelButtonColor = (color: string) => {
    setCancelButtonColorState(color);
    if (typeof window !== "undefined") {
      localStorage.setItem("vendor-cancel-button-color", color);
    }
  };

  const resetColors = () => {
    setAddButtonColorState(DEFAULT_ADD_COLOR);
    setCancelButtonColorState(DEFAULT_CANCEL_COLOR);
    if (typeof window !== "undefined") {
      localStorage.removeItem("vendor-add-button-color");
      localStorage.removeItem("vendor-cancel-button-color");
    }
  };

  return (
    <VendorButtonColorsContext.Provider
      value={{
        addButtonColor,
        cancelButtonColor,
        setAddButtonColor,
        setCancelButtonColor,
        resetColors,
      }}
    >
      {children}
    </VendorButtonColorsContext.Provider>
  );
}

export const useVendorButtonColors = () => {
  const context = useContext(VendorButtonColorsContext);
  
  // Call other color hooks
  const trainerColors = useTrainerColors();
  const institutionColors = useInstitutionButtonColors();
  const facultyColors = useFacultyColors();

  // Get active role
  const userRole = typeof window !== "undefined" ? localStorage.getItem("userRole") : null;

  if (userRole === "trainer") {
    return {
      addButtonColor: trainerColors.addButtonColor,
      cancelButtonColor: trainerColors.cancelButtonColor,
      setAddButtonColor: () => {},
      setCancelButtonColor: () => {},
      resetColors: () => {},
    };
  }

  if (userRole === "institution") {
    return {
      addButtonColor: institutionColors.addButtonColor,
      cancelButtonColor: institutionColors.cancelButtonColor,
      setAddButtonColor: () => {},
      setCancelButtonColor: () => {},
      resetColors: () => {},
    };
  }

  if (userRole === "faculty") {
    return {
      addButtonColor: facultyColors.addButtonColor,
      cancelButtonColor: facultyColors.cancelButtonColor,
      setAddButtonColor: () => {},
      setCancelButtonColor: () => {},
      resetColors: () => {},
    };
  }

  if (!context) {
    // Return default values if used outside provider (e.g. in institution view)
    return {
      addButtonColor: "#0070f3",
      cancelButtonColor: "#6b7280",
      setAddButtonColor: () => {},
      setCancelButtonColor: () => {},
      resetColors: () => {},
    };
  }
  return context;
};
